/**
 * One library entry, open: the document itself on the left (the PDF, or the
 * PDF rendition of a .docx) and, on the right, only what a firm user needs to
 * decide. The header holds the title, one status, a meta line and the "..."
 * menu of file actions. Callouts appear only when something needs action.
 * Details is the firm's description of the document, with a single Looks
 * Right / Save step; Activity is where it was used, its history, and (for
 * CaseBlink staff) the system's internals.
 */
import {
  Download,
  FileText,
  Folder,
  Loader2,
  RefreshCw,
  Sparkles,
  TriangleAlert,
  Upload
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FC } from "react";
import { useSelector } from "react-redux";
import { useDefaultLayout } from "react-resizable-panels";
import { toast } from "sonner";

import { firmKnowledgeApi, type FirmScope } from "@/api/firm-knowledge";
import { PdfPreview } from "@/components/common/PdfPreview";
import { ResizeEdge } from "@/components/ui/resize-edge";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup
} from "@/components/ui/resizable";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { MetaLine } from "@/components/ui/meta-line";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useViewportWidth } from "@/hooks/use-viewport-width";

import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import {
  clampDrawerWidth,
  DRAWER_DEFAULT_WIDTH,
  snapDrawerWidth
} from "../drawerWidth";
import {
  entryDrawerUidKey,
  useEntryDrawerStore,
  useEntryDrawerWidth
} from "../entryDrawerStore";
import {
  ACCEPTED_EXTENSIONS,
  acceptSuggestedTag,
  applySuggestion,
  CATEGORY_LABEL,
  categoryOf,
  convertedFromPdf,
  needsReview,
  pendingSuggestions,
  readinessOf,
  rejectionOf,
  renamedFrom,
  SUGGESTION_FIELDS,
  suggestionChanges
} from "../model";
import type { FirmLibraryEntry, SuggestionField } from "../types";
import { AnonymizedCopyNote, ClientDetailsBanner } from "./ClientDetailsBanner";
import {
  ConversionCallout,
  ConversionCompareDialog,
  ConversionMenuItems,
  useConversionActions
} from "./ConversionPanel";
import { EntryActionsMenu } from "./EntryActionsMenu";
import { EntryStatusBadge } from "./EntryBadges";
import { EntryCallout } from "./EntryCallout";
import {
  EntryFieldsForm,
  fieldValuesOf,
  sameFieldValues,
  type EntryFieldValues
} from "./EntryFieldsForm";
import { EntryHistory } from "./EntryHistory";
import { EntryTechnicalDetails } from "./EntryTechnicalDetails";
import { EntryUsagePanel } from "./EntryUsagePanel";
import {
  confirmLeaveLibraryEntry,
  LibraryDocxViewer,
  type LibraryDocxViewerHandle
} from "./LibraryDocxViewer";
import { SuggestionStatusRow } from "./SuggestionStatusRow";
import {
  EmptyTemplateCallout,
  TemplateFieldsPanel
} from "./TemplateFieldsPanel";

type Props = {
  entry: FirmLibraryEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canEdit: boolean;
  scope: FirmScope;
  onChanged: (entry: FirmLibraryEntry) => void;
  onDeleted: (entryId: string) => void;
  /** Called after an action that queues server work, so the list can poll. */
  onQueued: () => void;
  /** Open another entry in this drawer (an anonymized copy, its original). */
  onOpenEntry: (entryId: string) => void;
  /** Where the entry lives and the path the agents read it at. */
  location?: {
    label: string;
    agentPath: string;
    /** Opens Move To… for this entry; absent for readers. */
    onMove?: () => void;
  };
  /** CaseBlink staff: show the system's internals under Activity. */
  showTechnical?: boolean;
};

type Tab = "details" | "activity";

/** Below this window width the drawer is full width and has no resize edge. */
const RESIZABLE_FROM = 640;
/** Below this window width the document stacks above the details. */
const SPLIT_FROM = 1024;
const SPLIT_PANELS = ["entry-document", "entry-details"];

type BodyProps = Props & {
  entry: FirmLibraryEntry;
  /** Document and details side by side, divided by a draggable handle. */
  split: boolean;
  /** Where the split's sizes are remembered (per signed-in user). */
  splitId: string;
};

const download = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const EntryDrawerBody: FC<BodyProps> = ({
  entry,
  open,
  onOpenChange,
  canEdit,
  scope,
  onChanged,
  onDeleted,
  onQueued,
  onOpenEntry,
  location,
  showTechnical = false,
  split,
  splitId
}) => {
  const entryId = entry.id;
  const splitLayout = useDefaultLayout({ id: splitId, panelIds: SPLIT_PANELS });
  const [values, setValues] = useState<EntryFieldValues | null>(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [anonymizing, setAnonymizing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tab, setTab] = useState<Tab>("details");
  // Bumped when the file behind the entry is rebuilt, so the staff details
  // reload what they read off the old one.
  const [fileVersion, setFileVersion] = useState(0);
  // Suggestions the admin waved away for this entry; local, reset on entry
  // change or when the agent reads the document again. A dismissed rename is
  // also kept on the server (`suggested.dismissed`).
  const [localDismissed, setDismissed] = useState<ReadonlySet<SuggestionField>>(
    () => new Set()
  );
  const serverDismissed = entry.suggested?.dismissed;
  const dismissed = useMemo<ReadonlySet<SuggestionField>>(
    () => new Set([...localDismissed, ...(serverDismissed ?? [])]),
    [localDismissed, serverDismissed]
  );
  const replaceInput = useRef<HTMLInputElement | null>(null);
  const docxRef = useRef<LibraryDocxViewerHandle | null>(null);

  // Preview: the PDF (or rendition). 404 means it is still being made.
  const [pdf, setPdf] = useState<Blob | null>(null);
  const [pdfState, setPdfState] = useState<
    "loading" | "ready" | "pending" | "error"
  >("loading");

  // The body is keyed by entry, so all of this starts fresh per entry. Re-seed
  // the form only when the server row changes and the admin has not diverged
  // from it (a poll must not wipe typing).
  const baseline = useMemo(() => fieldValuesOf(entry), [entry]);
  const lastBaseline = useRef<EntryFieldValues | null>(null);
  useEffect(() => {
    const previous = lastBaseline.current;
    setValues((current) => {
      if (!current || !previous || sameFieldValues(current, previous))
        return baseline;
      return current;
    });
    lastBaseline.current = baseline;
  }, [baseline]);

  // Promoting or reverting the editable copy swaps the file behind the entry;
  // the preview belongs to the old one.
  const fileKind = entry.mime_type ?? null;
  useEffect(() => {
    setPdf(null);
    setPdfState("loading");
    setFileVersion((v) => v + 1);
  }, [fileKind]);

  // A fresh read supersedes whatever was dismissed from the previous one.
  const generatedAt = entry.suggested?.generated_at ?? null;
  useEffect(() => {
    setDismissed(new Set());
  }, [generatedAt]);

  const pdfAvailable = entry.has_pdf_rendition ?? false;
  const renderingReady = entry.rendering_status === "ready";
  // A Word entry (native, or converted from a PDF) opens as a document in the
  // docx viewer; only the rest preview as a PDF.
  const showsDocx = entry.authorable ?? false;
  useEffect(() => {
    if (!open || pdf || showsDocx) return undefined;
    if (!pdfAvailable && !renderingReady) {
      setPdfState(entry.rendering_status === "failed" ? "error" : "pending");
      return undefined;
    }
    let cancelled = false;
    setPdfState("loading");
    firmKnowledgeApi
      .getEntryPdf(entryId, scope)
      .then((blob) => {
        if (cancelled) return;
        setPdf(blob);
        setPdfState("ready");
      })
      .catch((error: { status?: number }) => {
        if (cancelled) return;
        setPdfState(error?.status === 404 ? "pending" : "error");
      });
    return () => {
      cancelled = true;
    };
  }, [
    open,
    entryId,
    pdf,
    showsDocx,
    pdfAvailable,
    renderingReady,
    entry.rendering_status,
    scope
  ]);

  const dirty = values ? !sameFieldValues(values, baseline) : false;
  const reviewing = canEdit && needsReview(entry);

  const save = async () => {
    if (!values) return;
    setSaving(true);
    try {
      const updated = await firmKnowledgeApi.updateEntry(
        entry.id,
        values,
        scope
      );
      onChanged(updated);
      toast.success("Saved.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const run = async (
    action: () => Promise<FirmLibraryEntry | void>,
    done?: string
  ) => {
    setBusy(true);
    try {
      const result = await action();
      if (result) onChanged(result);
      if (done) toast.success(done);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Something went wrong."
      );
    } finally {
      setBusy(false);
    }
  };

  const conversion = useConversionActions({ entry, scope, run, onQueued });

  // Accepting edits the form; Save is the one commit point. Text replaces,
  // tags merge (see `applySuggestion`). Until a person has looked at the
  // agent's own description there is nothing to compare it against. The
  // name is the one exception: the entry keeps the name it was uploaded
  // under, and the suggested name is a rename of its own, applied (and kept
  // in History) only when accepted, never by Accept All.
  const suggestion = entry.suggested ?? null;
  const pending = useMemo(
    () =>
      values && !reviewing
        ? pendingSuggestions(values, suggestion).filter(
            (field) => field !== "name" && !dismissed.has(field)
          )
        : [],
    [values, suggestion, dismissed, reviewing]
  );
  // While the agent's description awaits review only the rename is offered.
  const shownDismissed = useMemo<ReadonlySet<SuggestionField>>(
    () =>
      reviewing
        ? new Set([
            ...dismissed,
            ...SUGGESTION_FIELDS.filter((field) => field !== "name")
          ])
        : dismissed,
    [dismissed, reviewing]
  );

  const acceptRename = () =>
    run(async () => {
      const updated = await firmKnowledgeApi.acceptSuggestion(
        entry.id,
        { fields: ["name"] },
        scope
      );
      setValues((current) =>
        current ? { ...current, name: updated.name } : current
      );
      return updated;
    }, "Renamed.");

  const dismissRename = () => {
    setDismissed((current) => new Set(current).add("name"));
    void run(() =>
      firmKnowledgeApi.dismissSuggestion(entry.id, ["name"], scope)
    );
  };

  const acceptField = (field: SuggestionField) => {
    if (field === "name") {
      void acceptRename();
      return;
    }
    setValues((current) =>
      current ? applySuggestion(current, suggestion, field) : current
    );
  };

  const acceptAll = () => {
    setValues((current) => {
      if (!current) return current;
      return pending.reduce(
        (acc, field) => applySuggestion(acc, suggestion, field),
        current
      );
    });
  };

  const acceptTag = (tag: string) => {
    setValues((current) =>
      current ? acceptSuggestedTag(current, tag) : current
    );
  };

  const changes = useMemo(
    () => (values ? suggestionChanges(values, suggestion, pending) : []),
    [values, suggestion, pending]
  );

  const dismissField = (field: SuggestionField) => {
    if (field === "name") {
      dismissRename();
      return;
    }
    setDismissed((current) => new Set(current).add(field));
  };

  const fileRebuilt = () => {
    setPdf(null);
    setFileVersion((v) => v + 1);
  };

  const regenerate = () =>
    run(async () => {
      await firmKnowledgeApi.suggestForEntry(entry.id, scope);
      onQueued();
      return firmKnowledgeApi.getEntry(entry.id, scope);
    }, "The agent is re-reading the document.");

  const rerender = () =>
    run(async () => {
      await firmKnowledgeApi.rerenderEntry(entry.id, scope);
      onQueued();
      fileRebuilt();
      return firmKnowledgeApi.getEntry(entry.id, scope);
    }, "Re-rendering.");

  const replaceFile = (file: File | undefined) => {
    if (!file) return;
    const refused = rejectionOf(file);
    if (refused) {
      toast.error(`${refused}.`);
      return;
    }
    void run(async () => {
      const updated = await firmKnowledgeApi.replaceEntryFile(
        entry.id,
        file,
        scope
      );
      fileRebuilt();
      onQueued();
      return updated;
    }, "File replaced; re-rendering.");
  };

  const downloadOriginal = () =>
    run(async () => {
      const blob = await firmKnowledgeApi.getEntryFile(entry.id, scope);
      download(blob, entry.original_filename ?? `${entry.name}`);
    });

  const remove = () =>
    run(async () => {
      await firmKnowledgeApi.deleteEntry(entry.id, scope);
      onDeleted(entry.id);
      onOpenChange(false);
    }, "Removed from the library.");

  const anonymize = async () => {
    setAnonymizing(true);
    try {
      const { entry: copy, replaced } = await firmKnowledgeApi.anonymizeEntry(
        entry.id,
        scope
      );
      onChanged(copy);
      onChanged(await firmKnowledgeApi.getEntry(entry.id, scope));
      onQueued();
      toast.success(
        `Anonymized copy made: ${replaced} ${
          replaced === 1 ? "detail" : "details"
        } replaced with placeholders.`,
        { action: { label: "Open", onClick: () => onOpenEntry(copy.id) } }
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not anonymize."
      );
    } finally {
      setAnonymizing(false);
    }
  };

  const keepClientDetails = () =>
    run(
      () => firmKnowledgeApi.keepClientDetails(entry.id, scope),
      "Kept as is."
    );

  const markReviewed = () =>
    run(
      () => firmKnowledgeApi.markEntryReviewed(entry.id, scope),
      "Marked as reviewed."
    );

  if (!values) return null;

  const fallbackCategory = categoryOf({ ...entry, category: null });
  const readFailed = readinessOf(entry) === "attention";
  const suggestionFailed = canEdit && suggestion?.status === "failed";

  const preview = (
    <div
      className={
        split
          ? "flex h-full min-h-0 flex-col bg-fill-1"
          : "flex min-h-[18rem] flex-col border-b border-line-2 bg-fill-1"
      }
    >
      {entry.is_pdf && entry.conversion?.status === "pending" ? (
        <div
          className="flex items-center gap-2 border-b border-line-2 bg-info-soft px-3 py-2 text-sm text-info"
          data-testid="entry-converting-note"
        >
          <Loader2 className="size-3.5 animate-spin" />
          Converting to Word. This opens as an editable document once it is
          ready.
        </div>
      ) : null}
      <div className="min-h-0 flex-1">
        {showsDocx ? (
          <LibraryDocxViewer
            ref={docxRef}
            entry={entry}
            scope={scope}
            canEdit={canEdit}
            onSaved={onChanged}
          />
        ) : pdfState === "ready" && pdf ? (
          <PdfPreview
            file={pdf}
            ariaLabel={`Preview of ${entry.name}`}
            onDownload={() => downloadOriginal()}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-base text-fg-3">
            {pdfState === "loading" ? (
              <Loader2 className="size-5 animate-spin" />
            ) : (
              <FileText className="size-5" />
            )}
            {pdfState === "loading"
              ? "Opening…"
              : pdfState === "pending"
              ? "The preview is still being prepared."
              : entry.rendering_error
              ? `Could not prepare a preview: ${entry.rendering_error}`
              : "No preview is available for this file."}
            <div className="mt-2 flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void downloadOriginal()}
                disabled={busy}
              >
                <Download />
                Download Original
              </Button>
              {canEdit && pdfState === "error" ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void rerender()}
                  disabled={busy}
                >
                  <RefreshCw />
                  Try Again
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const details = (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="space-y-4 p-5">
          <div className="space-y-2 empty:hidden">
            <ClientDetailsBanner
              entry={entry}
              canEdit={canEdit}
              busy={busy}
              anonymizing={anonymizing}
              onAnonymize={() => void anonymize()}
              onKeep={() => void keepClientDetails()}
              onOpenEntry={onOpenEntry}
            />
            {readFailed ? (
              <EntryCallout
                tone="danger"
                icon={TriangleAlert}
                title="Couldn't read this file"
                data-testid="read-failed-callout"
                actions={
                  canEdit ? (
                    <>
                      <Button
                        variant="secondary"
                        disabled={busy}
                        onClick={() => void rerender()}
                      >
                        <RefreshCw /> Try Again
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={busy}
                        onClick={() => replaceInput.current?.click()}
                      >
                        <Upload /> Replace File
                      </Button>
                    </>
                  ) : null
                }
              >
                <p>
                  {entry.rendering_error ? `${entry.rendering_error}. ` : ""}
                  The agents can't use it until it reads cleanly.
                </p>
              </EntryCallout>
            ) : null}
            {suggestionFailed ? (
              <EntryCallout
                tone="warn"
                icon={Sparkles}
                title="The agent couldn't read this document"
                data-testid="suggestion-failed"
                actions={
                  <Button
                    variant="secondary"
                    disabled={busy}
                    onClick={() => void regenerate()}
                  >
                    <RefreshCw /> Try Again
                  </Button>
                }
              >
                <p>
                  {suggestion?.error ? `${suggestion.error}. ` : ""}
                  Describe it yourself below, or ask it to try again.
                </p>
              </EntryCallout>
            ) : null}
            <ConversionCallout
              conversion={conversion}
              canEdit={canEdit}
              busy={busy}
            />
            {canEdit ? (
              <EmptyTemplateCallout
                fields={entry.template_fields}
                category={categoryOf(entry)}
              />
            ) : null}
          </div>

          <Tabs
            value={tab}
            onValueChange={(next) => setTab(next as Tab)}
            className="gap-4"
          >
            <TabsList variant="pill">
              <TabsTrigger value="details" data-testid="entry-tab-details">
                Details
                {dirty ? (
                  <span
                    aria-hidden
                    title="Unsaved changes"
                    className="size-1.5 rounded-full bg-accent-seed"
                    data-testid="entry-dirty-dot"
                  />
                ) : null}
              </TabsTrigger>
              <TabsTrigger value="activity" data-testid="entry-tab-activity">
                Activity
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4">
              {reviewing ? (
                <p
                  className="flex items-center gap-2 text-base text-fg-2"
                  data-testid="review-prompt"
                >
                  <Sparkles className="size-3.5 shrink-0 text-purple" />
                  The agent filled this in from the document. Fix anything
                  that&apos;s off.
                </p>
              ) : null}
              {canEdit && !reviewing ? (
                <SuggestionStatusRow
                  suggestion={suggestion}
                  pendingCount={pending.length}
                  changes={changes}
                  busy={busy}
                  onAcceptAll={acceptAll}
                  onSuggest={() => void regenerate()}
                />
              ) : null}
              <EntryFieldsForm
                key={entry.id}
                values={values}
                onChange={setValues}
                disabled={!canEdit || saving}
                idPrefix={`entry-${entry.id}`}
                fallbackCategory={fallbackCategory}
                suggestions={
                  canEdit
                    ? {
                        suggestion,
                        dismissed: shownDismissed,
                        onAccept: acceptField,
                        onDismiss: dismissField,
                        onAcceptTag: acceptTag
                      }
                    : undefined
                }
              />
              <TemplateFieldsPanel
                fields={entry.template_fields}
                onReveal={
                  showsDocx
                    ? (token) => docxRef.current?.reveal(token) ?? false
                    : undefined
                }
              />
            </TabsContent>

            <TabsContent
              value="activity"
              className="divide-y divide-line-1 [&>*]:py-4 [&>*:first-child]:pt-0"
            >
              <EntryUsagePanel entry={entry} scope={scope} />
              <section className="space-y-2">
                <h3 className="text-sm font-medium">History</h3>
                <EntryHistory
                  entry={entry}
                  scope={scope}
                  canEdit={canEdit}
                  onRestored={(updated) => {
                    fileRebuilt();
                    onChanged(updated);
                    onQueued();
                  }}
                  onDownload={download}
                />
              </section>
              {showTechnical ? (
                <EntryTechnicalDetails
                  key={`${entry.id}:${fileVersion}:${entry.rendering_status}`}
                  entry={entry}
                  scope={scope}
                  agentPath={location?.agentPath}
                  conversion={conversion}
                  busy={busy}
                  onRerender={() => void rerender()}
                />
              ) : null}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {canEdit && tab === "details" ? (
        <div className="flex items-center justify-end gap-2 border-t border-line-2 px-5 py-3">
          {dirty ? (
            <Button
              variant="ghost"
              disabled={saving}
              onClick={() => setValues(baseline)}
            >
              Discard
            </Button>
          ) : null}
          {reviewing && !dirty ? (
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => void markReviewed()}
              data-testid="mark-reviewed"
            >
              Looks Right
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={!dirty || saving}
              onClick={() => void save()}
              data-testid="save-entry"
            >
              {saving ? <Loader2 className="animate-spin" /> : null}
              Save
            </Button>
          )}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <SheetHeader className="gap-1 border-b border-line-2 px-5 py-3 pr-14">
        <div className="flex min-w-0 items-center gap-2">
          <SheetTitle className="min-w-0 truncate text-base">
            {entry.name || entry.original_filename || "Untitled"}
          </SheetTitle>
          <EntryStatusBadge entry={entry} />
          <div className="ml-auto shrink-0">
            <EntryActionsMenu
              canEdit={canEdit}
              busy={busy}
              onDownload={() => void downloadOriginal()}
              onReplace={() => replaceInput.current?.click()}
              onReread={() => void regenerate()}
              onRemove={() => setConfirmDelete(true)}
              onMove={location?.onMove}
              conversionItems={
                <ConversionMenuItems
                  conversion={conversion}
                  canEdit={canEdit}
                  busy={busy}
                />
              }
            />
          </div>
        </div>
        <SheetDescription asChild>
          <div>
            <MetaLine
              className="flex-wrap"
              parts={[
                CATEGORY_LABEL[categoryOf(entry)],
                location ? (
                  <span
                    className="flex min-w-0 items-center gap-1"
                    data-testid="entry-location"
                  >
                    <Folder className="size-3.5 shrink-0" aria-hidden />
                    <span
                      className="truncate"
                      data-testid="entry-location-label"
                    >
                      {location.label}
                    </span>
                  </span>
                ) : null,
                entry.page_count
                  ? `${entry.page_count} ${
                      entry.page_count === 1 ? "page" : "pages"
                    }`
                  : null,
                convertedFromPdf(entry) ? (
                  <span data-testid="entry-converted-note">
                    Converted from PDF
                  </span>
                ) : null,
                renamedFrom(entry) ? (
                  <span
                    className="min-w-0 truncate"
                    data-testid="entry-uploaded-as"
                  >
                    Uploaded As {renamedFrom(entry)}
                  </span>
                ) : null,
                entry.source === "legacy_import"
                  ? "From your earlier templates"
                  : null,
                entry.anonymized_from ? (
                  <AnonymizedCopyNote entry={entry} onOpenEntry={onOpenEntry} />
                ) : null
              ]}
            />
          </div>
        </SheetDescription>
      </SheetHeader>

      <input
        ref={replaceInput}
        type="file"
        className="hidden"
        accept={ACCEPTED_EXTENSIONS.filter((e) => e !== ".zip").join(",")}
        aria-label="Replace file"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          replaceFile(file);
        }}
      />

      {split ? (
        <ResizablePanelGroup
          orientation="horizontal"
          defaultLayout={splitLayout.defaultLayout}
          onLayoutChanged={splitLayout.onLayoutChanged}
          className="min-h-0 flex-1"
        >
          <ResizablePanel id="entry-document" defaultSize="52" minSize="16rem">
            {preview}
          </ResizablePanel>
          <ResizableHandle />
          <ResizablePanel id="entry-details" defaultSize="48" minSize="22rem">
            {details}
          </ResizablePanel>
        </ResizablePanelGroup>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1">
          {preview}
          {details}
        </div>
      )}

      <ConversionCompareDialog conversion={conversion} />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove From the Library?</AlertDialogTitle>
            <AlertDialogDescription>
              The agents stop seeing this document at once. Drafts that already
              used it are not changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmDelete(false);
                void remove();
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const EntryDrawer: FC<Props> = ({
  entry,
  open,
  onOpenChange,
  onOpenEntry,
  ...rest
}) => {
  const uid = useSelector(lawyerSelectors.selectUid);
  const uidKey = entryDrawerUidKey(uid);
  const storedWidth = useEntryDrawerWidth(uid);
  const setStoredWidth = useEntryDrawerStore((s) => s.setWidth);
  const viewport = useViewportWidth();
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const resizable = viewport >= RESIZABLE_FROM;
  const width = dragWidth ?? clampDrawerWidth(storedWidth, viewport);

  // Closing the drawer or opening another entry unmounts the docx editor;
  // unsaved edits there would go with it.
  const requestOpenChange = (next: boolean) => {
    if (!next && entry && !confirmLeaveLibraryEntry(entry.id)) return;
    onOpenChange(next);
  };
  const requestOpenEntry = (entryId: string) => {
    if (entry && entryId !== entry.id && !confirmLeaveLibraryEntry(entry.id))
      return;
    onOpenEntry(entryId);
  };

  return (
    // Non-modal: the docx editor types through a hidden view it mounts on
    // document.body, and a modal sheet's focus trap would pull focus back out.
    <Sheet open={open} onOpenChange={requestOpenChange} modal={false}>
      <SheetContent
        side="right"
        scrimOpen={open}
        style={resizable ? { width } : undefined}
        className="gap-0 overflow-hidden p-0 max-sm:w-full! sm:max-w-none!"
        data-testid="entry-drawer"
      >
        {resizable ? (
          <ResizeEdge
            width={width}
            onDrag={setDragWidth}
            onCommit={(next) => setStoredWidth(uidKey, next)}
            clamp={(w) => clampDrawerWidth(w, viewport)}
            snap={(w) => snapDrawerWidth(w, viewport)}
            defaultWidth={DRAWER_DEFAULT_WIDTH}
            label="Resize Drawer"
            testId="entry-drawer-resize"
          />
        ) : null}
        {entry ? (
          <EntryDrawerBody
            key={entry.id}
            entry={entry}
            open={open}
            onOpenChange={onOpenChange}
            onOpenEntry={requestOpenEntry}
            split={viewport >= SPLIT_FROM}
            splitId={`firmKnowledge.entryDrawer.${uidKey}`}
            {...rest}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  );
};
